import { isDefined } from 'common';
import { FlowDefinition } from 'vivaa-api';

import { BlockType, ObsoleteBlockType, type UiBlockType } from '../../types';
import { type FlowBlockConfig } from '../types';

interface RetiredBlockProps {
  /**
   * In order to be intentional, you are required to specify a replacement block type and a function
   * even if it is undefined.
   */
  replacementBlockType: BlockType | undefined;
  getUpdatedPartialBlockConfig: ((oldConfig: any) => Partial<FlowBlockConfig>) | undefined;
}
const NO_REPLACEMENT_BLOCK_TYPE = undefined;

/**
 * TODO - strip all fields other than `retiredBlockProps`, and rename to match.
 */
export const RETIRED_BLOCK_PROPS_BY_BLOCK_TYPE: Partial<Record<UiBlockType, RetiredBlockProps>> = {
  // TODO: Deprecate this once GenerateFile is live and CombinePDFs has been moved to FileConversion
  [BlockType.DocumentTemplate]: {
    replacementBlockType: BlockType.GenerateFile,
    getUpdatedPartialBlockConfig: (
      oldConfig: FlowDefinition.DocumentTemplateBlockConfig
    ): Partial<FlowDefinition.GenerateFile.GenerateFileBlockConfig> => {
      if (oldConfig.generateDocumentMode === FlowDefinition.GenerateDocumentMode.DocumentTemplate) {
        return {
          mode: FlowDefinition.GenerateFile.Mode.DocumentTemplate,
          documentTemplateId: oldConfig.documentTemplateId,
          valuesByDocumentTemplateKey: oldConfig.valuesByDocumentTemplateKey,
        };
      }
      if (oldConfig.generateDocumentMode === FlowDefinition.GenerateDocumentMode.Markdown) {
        return {
          mode: FlowDefinition.GenerateFile.Mode.Markdown,
          markdown: oldConfig.markdown,
        };
      }
      // CombinePdf has no equivalent in GenerateFile — fall back to default config
      return {};
    },
  },
  [BlockType.SubFlowV1]: {
    replacementBlockType: BlockType.SubFlowV2,
    getUpdatedPartialBlockConfig: (
      oldConfig: FlowDefinition.SubFlowV1Block['config']
    ): Partial<FlowDefinition.SubFlowV2Block['config']> => {
      return {
        flowId: oldConfig.flowId,
        flowDefinitionId: oldConfig.flowDefinitionId,
        versionTarget: oldConfig.versionTarget,
      };
    },
  },
  [BlockType.Wait]: {
    replacementBlockType: BlockType.WaitV2,
    getUpdatedPartialBlockConfig: (
      oldConfig: FlowDefinition.WaitBlock['config']
    ): Partial<FlowDefinition.WaitBlockV2['config']> => {
      return {
        duration: {
          value: oldConfig.waitTimeMillis / 1000,
          units: FlowDefinition.DurationUnits.Seconds,
        },
      };
    },
  },
  [ObsoleteBlockType.ObsoleteSendToLabelboxForReview]: {
    replacementBlockType: BlockType.HumanTask,
    getUpdatedPartialBlockConfig: undefined,
  },
  [ObsoleteBlockType.ObsoleteWaitForAvailableSchedulingSlot]: {
    replacementBlockType: NO_REPLACEMENT_BLOCK_TYPE,
    getUpdatedPartialBlockConfig: undefined,
  },
  [BlockType.Ocr]: {
    replacementBlockType: BlockType.OcrV2,
    getUpdatedPartialBlockConfig: undefined,
  },
  [BlockType.AsynchronousExtractJob]: {
    replacementBlockType: NO_REPLACEMENT_BLOCK_TYPE,
    getUpdatedPartialBlockConfig: undefined,
  },
  [BlockType.Scheduling]: {
    replacementBlockType: BlockType.Form,
    getUpdatedPartialBlockConfig: undefined,
  },
  [BlockType.VoiceCall]: {
    replacementBlockType: BlockType.VoiceCallV2,
    getUpdatedPartialBlockConfig: undefined,
  },
  [BlockType.AuthorizationSubmission]: {
    replacementBlockType: NO_REPLACEMENT_BLOCK_TYPE,
    getUpdatedPartialBlockConfig: undefined,
  },
  [BlockType.IteratorV1]: {
    replacementBlockType: BlockType.IteratorV2,
    getUpdatedPartialBlockConfig: (
      oldConfig: FlowDefinition.IteratorV1Block['config']
    ): Partial<FlowDefinition.IteratorV2Block['config']> => {
      return {
        items: oldConfig.items,
        iterationStepDuration: isDefined(oldConfig.iterationStepDuration)
          ? {
              value: String(oldConfig.iterationStepDuration.value),
              units: oldConfig.iterationStepDuration.units,
            }
          : undefined,
        subFlow:
          oldConfig.action.type === FlowDefinition.BlockType.SubFlowV1 &&
          isDefined(oldConfig.action.config.flowId) &&
          isDefined(oldConfig.action.config.versionTarget)
            ? {
                flowId: oldConfig.action.config.flowId,
                flowDefinitionId: oldConfig.action.config.flowDefinitionId,
                versionTarget: oldConfig.action.config.versionTarget,
                arguments: [],
              }
            : undefined,
      };
    },
  },
  [BlockType.RestApiClassicAuthorization]: {
    replacementBlockType: BlockType.Rest,
    getUpdatedPartialBlockConfig: undefined,
  },
  [BlockType.Form]: {
    replacementBlockType: BlockType.FormV2,
    getUpdatedPartialBlockConfig: (
      oldConfig: FlowDefinition.FormBlock['config']
    ): Partial<FlowDefinition.FormV2Block['config']> => {
      const { runMode: _runMode, ...rest } = oldConfig;
      return rest;
    },
  },
  [BlockType.WaitForFormCompletion]: {
    replacementBlockType: BlockType.FormV2,
    getUpdatedPartialBlockConfig: undefined,
  },
};
